// Centralized helpers for extracting user-displayable error messages from ApiResponse or unknown values.
import { ApiResponse, ProblemDetails } from './agwasuite-api';
import type { KeptText } from '../language/kept-text-value';

export function extractProblemMessage(
  problem?: ProblemDetails
): string | undefined {
  if (!problem) return undefined;
  if (problem.title) return problem.title;
  if (problem.detail) return problem.detail;
  if (typeof problem.status === 'number')
    return `Request failed (${problem.status})`;
  return undefined;
}

export function toErrorMessage<T>(resp: ApiResponse<T>): string {
  if (resp.success) return '';
  const probMsg = extractProblemMessage(resp.problem);
  if (probMsg) return probMsg;
  if (typeof resp.error === 'string' && resp.error.trim()) return resp.error;
  const data: unknown = resp.errorData;
  if (data) {
    if (typeof data === 'string') return data;
    if (typeof (data as { message?: unknown }).message === 'string')
      return String((data as { message?: unknown }).message);
    const asObj = data as Record<string, unknown>;
    const candidateErrors = (asObj as { errors?: unknown }).errors;
    if (Array.isArray(candidateErrors)) {
      return candidateErrors.map(String).join(', ');
    }
    if (candidateErrors && typeof candidateErrors === 'object') {
      const firstKey = Object.keys(candidateErrors)[0];
      const firstVal = (candidateErrors as Record<string, unknown[]>)[firstKey];
      if (Array.isArray(firstVal) && firstVal.length)
        return String(firstVal[0]);
    }
  }
  return 'An unexpected error occurred.';
}

/**
 * What an error toast shows for a failed response: the same message `toErrorMessage` chooses, but as
 * the server's text still tagged (KeptText) whenever that message is the response's `error`, so the
 * toast resolves it once and shows a person tag as the person's current name (@VOC-SERVER-002).
 * Any other choice (a problem title, a field from the payload, the generic fallback) is returned as
 * the plain string `toErrorMessage` gives, and shown as it stands.
 */
export function toErrorNotice<T>(resp: ApiResponse<T>): KeptText | string {
  const message = toErrorMessage(resp);
  return !resp.success && resp.errorKept && message === resp.error
    ? resp.errorKept
    : message;
}

export function ensureErrorString(input: unknown): string {
  if (typeof input === 'string') return input;
  if (!input) return '';
  if (input instanceof Error) return input.message;
  if (typeof (input as { message?: unknown }).message === 'string')
    return String((input as { message?: unknown }).message);
  try {
    return JSON.stringify(input);
  } catch {
    return String(input);
  }
}
